import React, {useEffect, useState} from "react";
import {Box, Fade, IconButton, InputBase, MenuItem, Modal, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {
  controlSx, FieldHelper, FieldLabel, modalCancelSx, ModalButton, modalPaperSx, modalSaveSx, selectSx,
} from "../../../components/modals/formFieldShared";
import {SERVICE_FIELDS} from "../serviceFields";

const toFormConfiguration = (configuration) => ({
  name: configuration?.name || "",
  ...Object.fromEntries(SERVICE_FIELDS.map(field => [field.name, configuration?.[field.name] || ""])),
  organizationId: configuration?.organizationId ?? null,
});

export const BackendConfigurationDialog = ({open, handleClose, selectedConfiguration, organizations, onSubmit}) => {
  const [configuration, setConfiguration] = useState(toFormConfiguration());
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(selectedConfiguration?.id);

  useEffect(() => {
    setConfiguration(toFormConfiguration(selectedConfiguration));
  }, [selectedConfiguration, open]);

  const onChange = (e) => {
    const {name, value} = e.target;
    setConfiguration(prev => ({...prev, [name]: value}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!configuration.name.trim()) return;
    setLoading(true);
    const success = await onSubmit({
      ...configuration,
      name: configuration.name.trim(),
      ...Object.fromEntries(SERVICE_FIELDS.map(field => [field.name, configuration[field.name].trim()])),
    });
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Fade in={open}>
        <Box component={"form"} onSubmit={handleSubmit} sx={modalPaperSx(480)}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{p: 3, pb: 2}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              {isEdit ? "Edit" : "Add"} Backend Configuration
            </Typography>
            <IconButton size={"small"} onClick={loading ? undefined : handleClose} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{px: 3, pb: 1, overflow: "auto"}}>
            <Box>
              <FieldLabel required>Name</FieldLabel>
              <Box sx={controlSx}>
                <InputBase
                  name={"name"} autoFocus placeholder={"e.g. Production backend"} value={configuration.name}
                  onChange={onChange} inputProps={{"aria-label": "Name"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            {SERVICE_FIELDS.map(field => (
              <Box key={field.name}>
                <FieldLabel>{field.label}</FieldLabel>
                <Box sx={controlSx}>
                  <InputBase
                    name={field.name} placeholder={field.placeholder} value={configuration[field.name]}
                    onChange={onChange} inputProps={{"aria-label": field.label}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                  />
                </Box>
              </Box>
            ))}

            <Box>
              <FieldLabel>Organization Access</FieldLabel>
              <Select
                value={configuration.organizationId ?? ""}
                onChange={(e) => setConfiguration(prev => ({...prev, organizationId: e.target.value || null}))}
                sx={selectSx}
                MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
              >
                <MenuItem value={""}>Public (all users)</MenuItem>
                {organizations?.map(org => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
              <FieldHelper>Leave empty to make this configuration available to all users</FieldHelper>
            </Box>
          </Stack>

          <Stack direction={"row"} gap={1.5} justifyContent={"flex-end"} sx={{p: 3, pt: 2}}>
            <ModalButton onClick={loading ? undefined : handleClose} sx={modalCancelSx}>Cancel</ModalButton>
            <ModalButton type={"submit"} disabled={loading} sx={modalSaveSx}>
              {isEdit ? "Update" : "Add Configuration"}
            </ModalButton>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};
